import { Dialog } from "radix-ui";
import Button from "@/components/bricks/button/Button";
import ArrowLeftIcon from "../../components/icons/ArrowLeftIcon";
import CheckIcon from "../../components/icons/CheckIcon";
import { useContext, useState } from "react";
import {
  DropdownContent,
  DropdownItem,
  DropdownItemIcon,
  DropdownRoot,
  DropdownSeparator,
  DropdownTrigger,
} from "../../components/dropdown/Dropdown";
import {
  TabsContent,
  TabsList,
  TabsRoot,
  TabsTrigger,
} from "../../components/tabs/Tabs";
import PageTitle from "@/components/PageTitle";
import FirmaIcon from "../../components/icons/FirmaIcon";
import Sidebar from "../../components/pages/firma/id/Sidebar";
import Stamoplysninger from "../../components/pages/firma/id/Stamoplysninger/Stamoplysninger";
import Tilsyn from "../../components/pages/firma/id/Tilsyn/Tilsyn";
import Disciplinaert from "../../components/pages/firma/id/Disciplinaert/Disciplinaert";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@apollo/client";
import { GET_FIRMA_BY_ID_QUERY } from "../../api/firma/queries";
import Spinner from "../../components/spinner/Spinner";
import { PagedFirmaQueryContext } from "../../context/PagedFirmaQueryProvider";
import { IDPagination } from "../../components/pagination/IDPagination";
import EmailIcon from "../../components/icons/EmailIcon";
import ReportIcon from "../../components/icons/ReportIcon";
import { ReportLink } from "../../components/ReportLink";
import FlytfirmaIcon from "../../components/icons/FlytfirmaIcon";
import Modal, {
  ModalContext,
  ModalProvider,
  ModalTrigger,
} from "../../components/Modal/Modal";
import SendEmailModal from "../../components/sendEmail/SendEmailModal";
import { FirmaDto } from "../../interfaces/schema";
import { UserQueryContext } from "../../context/UserContext";
import AccessDenied from "../../components/AccessDenied";
import { useReports } from "../../hooks/useQueries";
import { transformQueryDataPerioder } from "../../util/apollo";
import { useConfirm } from "../../components/confirm/useConfirm";
import { post } from "../../api/util";
import SyncIcon from "../../components/icons/SyncIcon";
import { StickyFooter } from "../../components/stickyFooter/modalStickyFooter";
import FirmaMasseFlyt, {
  IFirmaMasseFlytProps,
} from "../../components/wizards/FirmaMasseFlyt/FirmaMasseFlyt";

const FirmaById = () => {
  // navigation
  const params = useParams();
  const navigate = useNavigate();
  const { id } = params;

  // context
  const pagedFirmaQueryContext = useContext(PagedFirmaQueryContext);
  const { user, hasPermission } = useContext(UserQueryContext);

  const maaIkkeOpdaterBC = !(
    hasPermission(user, "AdminWrite") || hasPermission(user, "KursusWrite")
  );

  // state
  const [clear, setClear] = useState(false);
  const [activeModal, setActiveModal] = useState<ActiveModalTypes>("sendemail");

  // hooks and stuff
  const { reports: allReports } = useReports();
  const reports = allReports?.filter((r) => r.uiPath === "aika/web/firma");
  reports?.sort((a, b) => a.title.localeCompare(b.title));

  const { confirmDialog } = useConfirm();

  // load some stuff
  const [data, setData] = useState<
    | {
        firma: FirmaDto;
      }
    | undefined
  >(undefined);
  const { loading, error, refetch } = useQuery<{ firma: FirmaDto }>(
    GET_FIRMA_BY_ID_QUERY(),
    {
      variables: { id },
      notifyOnNetworkStatusChange: true,
      onCompleted: (d) => {
        const transformed = transformQueryDataPerioder(d);
        setData(transformed);
      },
    },
  );
  if (loading) return <Spinner />;
  if (error) return <div>noget gik galt 😔</div>;
  if (!data) return null;
  const { firma } = data;
  if (!firma || !id) return <div>Intet firma fundet 😯</div>;

  return (
    <div className="l-main__content">
      <PageTitle title={firma.navn1} />
      <header className="navbar navbar--sub">
        <div className="col d-flex align-items-center gap-3">
          <Button
            icon
            color="secondary"
            aria-label="Tilbage"
            onClick={() => {
              navigate("/firma");
            }}
          >
            <ArrowLeftIcon />
          </Button>
          <h2 className="mx-1 d-flex align-items-center gap-2">
            <span className="d-flex align-items-center me-1">
              <FirmaIcon height={26} />
            </span>
            {firma.navn1}
          </h2>
        </div>
        <div className="col d-flex align-items-center justify-content-end gap-5">
          <ModalProvider>
            <DropdownRoot className="c-dropdown-default--actions">
              <DropdownTrigger>
                <span className="badge badge--fixed-size ms-n2">
                  <span className="ms-n2">
                    <CheckIcon />
                  </span>
                  <span>Aktuelt firma</span>
                </span>
                <span className="fw-semibold">Handlinger</span>
              </DropdownTrigger>
              <DropdownContent>
                <DropdownItem asChild>
                  <ModalTrigger
                    onClick={() => {
                      setClear(true);
                      setActiveModal("sendemail");
                    }}
                  >
                    <DropdownItemIcon>
                      <EmailIcon />
                    </DropdownItemIcon>
                    <span>Send e-mail ...</span>
                  </ModalTrigger>
                </DropdownItem>

                <DropdownSeparator />
                <DropdownItem asChild>
                  <ModalTrigger
                    onClick={() => setActiveModal("mergefirma")}
                    disabled={
                      !hasPermission(user, "BasisWrite") &&
                      !hasPermission(user, "KursusWrite")
                    }
                  >
                    <DropdownItemIcon>
                      <FirmaIcon height={17} />
                    </DropdownItemIcon>
                    <span>Inkorporér andet firma ...</span>
                  </ModalTrigger>
                </DropdownItem>
                <DropdownItem
                  asChild
                  disabled={
                    !hasPermission(user, "BasisWrite") &&
                    !hasPermission(user, "KursusWrite")
                  }
                >
                  <ModalTrigger
                    onClick={() => setActiveModal("firma-masseflytning")}
                    disabled={
                      !hasPermission(user, "BasisWrite") &&
                      !hasPermission(user, "KursusWrite")
                    }
                  >
                    <DropdownItemIcon>
                      <FlytfirmaIcon />
                    </DropdownItemIcon>
                    <span>Masseflytning af medarbejdere</span>
                  </ModalTrigger>
                </DropdownItem>
                <DropdownItem
                  onClick={async () => {
                    const confirm = await confirmDialog(
                      "Opdater BC",
                      `Her kan du som en éngangshandling overføre opdaterede debitoroplysninger til Business Central for det viste firma. 
                      Vær opmærksom på, at debitoropdateringen i BC kører tre gange i døgnet.`,
                      "Annuller",
                      "OK",
                    );

                    if (!confirm) {
                      return;
                    }

                    try {
                      const firmaNr = firma.firmaNr;
                      post({
                        query: `syncdebitor/firma/${firmaNr}`,
                      }).then(
                        () => {},
                        async () => {
                          await confirmDialog(
                            "Noget gik galt ..",
                            `Noget gik galt, da vi forsøgte at synkronisere med BC. 
                          Kontakt Systemadministrator, hvis problemet fortsætter.`,
                            "Luk",
                            "OK",
                          );
                        },
                      );
                      // eslint-disable-next-line @typescript-eslint/no-unused-vars
                    } catch (error) {
                      // swallow exception
                    }
                  }}
                  disabled={maaIkkeOpdaterBC}
                >
                  <DropdownItemIcon>
                    <SyncIcon />
                  </DropdownItemIcon>
                  <span>Opdater BC ...</span>
                </DropdownItem>

                {reports && (
                  <>
                    <DropdownSeparator />
                    {reports &&
                      reports.map((r) => {
                        return (
                          <DropdownItem asChild key={r.id}>
                            <ReportLink
                              url={`${r.url}?id=${firma.id}`}
                              className="c-dropdown-default-item "
                            >
                              <DropdownItemIcon>
                                <ReportIcon />
                              </DropdownItemIcon>
                              <span>{r.title}</span>
                            </ReportLink>
                          </DropdownItem>
                        );
                      })}
                  </>
                )}
              </DropdownContent>
            </DropdownRoot>
            <IDPagination
              pagedContext={pagedFirmaQueryContext}
              currentSelection={firma}
            />
            {activeModal === "sendemail" && (
              <SendEmailModal
                type="firma"
                contextData={{
                  selectedIds: [id],
                  email: firma.email ?? undefined,
                  totalCount: firma.email ? 1 : 0,
                }}
                clear={clear}
                setClear={setClear}
              />
            )}
            {activeModal === "firma-masseflytning" && (
              <FirmaMasseFlytWrapper
                firma={firma}
                onSuccess={() => {
                  refetch();
                }}
                key={firma.id}
              />
            )}
            {activeModal === "mergefirma" && (
              <Modal
                title={
                  <>
                    <FirmaIcon height={20} /> Inkorporér andet firma
                  </>
                }
                description=""
              >
                <div className="c-modal-container">
                  <div className="c-item-list c-modal-container-inner">
                    Kontakt Systemadministrator for at få bistand til at
                    sammenlægge data for to firmaer, som er samme firma.
                  </div>
                </div>
                <StickyFooter>
                  <Dialog.Close asChild>
                    <Button color="primary" type="button" onClick={() => {}}>
                      Ok
                    </Button>
                  </Dialog.Close>
                </StickyFooter>
              </Modal>
            )}
          </ModalProvider>
        </div>
      </header>
      <div className="l-main-row">
        <Sidebar firma={firma} />
        <main className="col overflow-hidden">
          <TabsRoot
            localStorageKey="firma-id-tabs"
            defaultValue="stamoplysninger"
          >
            <TabsList>
              <div className="container">
                <TabsTrigger
                  value="stamoplysninger"
                  isDisabled={!hasPermission(user, "BasisRead")}
                >
                  Stamoplysninger
                </TabsTrigger>
                <TabsTrigger
                  value="tilsyn"
                  isDisabled={!hasPermission(user, "TilsynRead")}
                >
                  Tilsyn
                </TabsTrigger>
                <TabsTrigger
                  value="disciplinaert"
                  isDisabled={!hasPermission(user, "DisciplinaertRead")}
                >
                  Disciplinært
                </TabsTrigger>
              </div>
            </TabsList>
            <TabsContent value="stamoplysninger">
              {hasPermission(user, "BasisRead") ? (
                <Stamoplysninger firma={firma} invalidateData={refetch} />
              ) : (
                <AccessDenied />
              )}
            </TabsContent>
            <TabsContent value="tilsyn">
              {hasPermission(user, "TilsynRead") ? (
                <Tilsyn firma={firma} />
              ) : (
                <AccessDenied />
              )}
            </TabsContent>
            <TabsContent value="disciplinaert">
              {hasPermission(user, "DisciplinaertRead") ? (
                <Disciplinaert firma={firma} />
              ) : (
                <AccessDenied />
              )}
            </TabsContent>
          </TabsRoot>
        </main>
      </div>
    </div>
  );
};

export default FirmaById;

// Wrapped so we can get modalOpen from context within correct provider
// and reset the modal when it's closed
function FirmaMasseFlytWrapper(props: IFirmaMasseFlytProps) {
  const { modalOpen } = useContext(ModalContext);
  return (
    <FirmaMasseFlyt
      firma={props.firma}
      onSuccess={props.onSuccess}
      key={modalOpen.toString()}
    />
  );
}
